import React from "react";
import { motion } from "framer-motion";
import { Leaf, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import { SHOP_PHOTO_WIDE } from "@/i18n/translations";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Hero = () => {
  const { t } = useLang();
  const h = t.hero;

  return (
    <section id="home" className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid="hero">
      {/* Background shop photo */}
      <div className="absolute inset-0">
        <img src={SHOP_PHOTO_WIDE} alt="Care For Me spa interior" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-brand-ink/55" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-deep/70 via-brand-ink/30 to-transparent" />
      </div>
      <div className="grain absolute inset-0 z-10" />

      <div className="relative z-20 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-24 pb-16">
        <div className="max-w-2xl">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full glass-dark border border-white/20 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-stone"
          >
            <Sparkles className="h-3.5 w-3.5 text-bamboo" /> {h.badge}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.08 }}
            className="mt-6 font-heading text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.02] tracking-tight text-stone"
          >
            {h.title_1}
            <span className="block italic text-bamboo-soft mt-1">{h.title_2}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.18 }}
            className="mt-7 text-base sm:text-lg text-stone/85 leading-relaxed max-w-xl"
          >
            {h.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.28 }}
            className="mt-9 flex flex-col sm:flex-row gap-3"
          >
            <Button
              data-testid="hero-book-btn"
              onClick={() => scrollTo("grooming")}
              className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-13 px-8 py-6 text-base no-a11y-underline group"
            >
              {h.cta_primary}
              <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
            </Button>
            <Button
              data-testid="hero-nagayu-btn"
              onClick={() => scrollTo("nagayu")}
              variant="outline"
              className="rounded-full border-2 border-bamboo bg-transparent text-stone hover:bg-bamboo hover:text-brand-cream font-bold h-13 px-8 py-6 text-base no-a11y-underline"
            >
              <Leaf className="h-4 w-4 mr-2" /> {h.cta_secondary}
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.45 }}
            className="mt-12 flex flex-wrap gap-x-7 gap-y-2"
          >
            {[h.stat_1, h.stat_2, h.stat_3].map((s, i) => (
              <span key={i} className="flex items-center gap-2 text-sm font-semibold text-stone/80">
                <Leaf className="h-4 w-4 text-bamboo" /> {s}
              </span>
            ))}
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-background to-transparent z-10" />
    </section>
  );
};
